import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import RadioGroup from 'tio-ui/components/radio-group';
import Select from 'tio-ui/components/select';
import { VStack } from 'tio-ui/components/layout';
import Button from 'tio-ui/components/button';
import type Owner from '@ember/owner';
import type CompanyModel from 'tio-data/models/company';

const ACCESS_TYPE_OPTIONS = [
  { key: 'include', label: 'Include only selected companies' },
  { key: 'exclude', label: 'Exclude selected companies' },
];

interface PartnerVisibilityConfigSignature {
  Args: {
    visibilityLevel: 'private' | 'partner' | 'global';
    partnerAccessConfig: number[];
    availableCompanies?: CompanyModel[];

    // Event handlers
    onVisibilityLevelChange: (level: 'private' | 'partner' | 'global') => void;
    onPartnerAccessConfigChange: (config: number[]) => void;
  };
}

export default class PartnerVisibilityConfig extends Component<PartnerVisibilityConfigSignature> {
  @tracked selectedCompanies: string[] = [];
  @tracked useExcludeList = false;

  constructor(owner: Owner, args: PartnerVisibilityConfigSignature['Args']) {
    super(owner, args);
    this.initializeState();
  }

  initializeState() {
    const config = this.args.partnerAccessConfig || [];
    if (config.length > 0) {
      const firstValue = config[0];
      if (firstValue && firstValue < 0) {
        // Negative values = exclude list
        this.useExcludeList = true;
        this.selectedCompanies = config.map((id) => Math.abs(id).toString());
      } else {
        // Positive values = include list
        this.useExcludeList = false;
        this.selectedCompanies = config.map((id) => id.toString());
      }
    }
  }

  get companyOptions() {
    return (
      this.args.availableCompanies?.map((company) => ({
        key: company.id?.toString() || '',
        label: company.name,
      })) || []
    );
  }

  get accessDescription() {
    if (this.args.visibilityLevel !== 'partner') return '';

    const count = this.selectedCompanies.length;
    if (count === 0) {
      return 'All partner companies can see this report';
    }

    if (this.useExcludeList) {
      return `All partner companies can see this report except ${count} selected ${count === 1 ? 'company' : 'companies'}`;
    } else {
      return `Only ${count} selected ${count === 1 ? 'company' : 'companies'} can see this report`;
    }
  }

  handleVisibilityLevelChange = (level: string) => {
    this.args.onVisibilityLevelChange(level as 'private' | 'partner' | 'global');

    // Clear partner config if not partner visibility
    if (level !== 'partner') {
      this.selectedCompanies = [];
      this.args.onPartnerAccessConfigChange([]);
    }
  };

  handleAccessTypeChange = (useExclude: boolean) => {
    this.useExcludeList = useExclude;
    this.updatePartnerConfig();
  };

  handleRadioChange = (value: string) => {
    this.handleAccessTypeChange(value === 'exclude');
  };

  handleCompanySelectionChange = (companyIds: string[]) => {
    this.selectedCompanies = companyIds;
    this.updatePartnerConfig();
  };

  clearAllCompanies = () => {
    this.selectedCompanies = [];
    this.updatePartnerConfig();
  };

  updatePartnerConfig() {
    if (this.args.visibilityLevel !== 'partner') {
      this.args.onPartnerAccessConfigChange([]);
      return;
    }

    if (this.selectedCompanies.length === 0) {
      // Empty array = all partners can see
      this.args.onPartnerAccessConfigChange([]);
      return;
    }

    const config = this.useExcludeList
      ? this.selectedCompanies.map((id) => -Math.abs(parseInt(id, 10))) // Negative for exclude
      : this.selectedCompanies.map((id) => Math.abs(parseInt(id, 10))); // Positive for include

    this.args.onPartnerAccessConfigChange(config);
  }

  <template>
    <div
      class="border tio-border-subtle rounded-lg p-4 tio-bg-sunken"
      data-test-partner-visibility-config
    >
      <VStack>
        <h4 class="tio-text-sm-medium tio-fg-primary">Partner Access Configuration</h4>

        {{! Access Type Toggle }}
        <RadioGroup
          @label="Access Type"
          @value={{if this.useExcludeList "exclude" "include"}}
          @onChange={{this.handleRadioChange}}
          @options={{ACCESS_TYPE_OPTIONS}}
        />

        {{! Company Selection }}
        <div>
          <Select
            @label={{if this.useExcludeList "Companies to Exclude" "Companies to Include"}}
            @items={{this.companyOptions}}
            @selectedKeys={{this.selectedCompanies}}
            @onChangeKeys={{this.handleCompanySelectionChange}}
            @multiple={{true}}
            @placeholder="{{if
              this.useExcludeList
              'Select companies to exclude...'
              'Select companies to include...'
            }}"
          />
        </div>

        {{! Clear Selection Button }}
        {{#if this.selectedCompanies.length}}
          <div class="tio-flex-end">
            <Button
              @appearance="outlined"
              @size="sm"
              {{on "click" this.clearAllCompanies}}
              data-test-clear-all
            >
              Clear All
            </Button>
          </div>
        {{/if}}

        {{! Access Description }}
        <div class="text-sm tio-fg-muted italic" data-test-access-description>
          {{this.accessDescription}}
        </div>
      </VStack>
    </div>
  </template>
}
